import { Injectable } from '@angular/core';

export interface ImagePricingTier {
  id: string;
  name: string;
  type: 'one-time' | 'monthly' | 'yearly';
  images: number;
  price: number;
  originalPrice?: number; // For discounted yearly plans
  savings?: number; // Percentage saved
  popular?: boolean;
  description?: string;
  pricePerImage?: number; // Calculated price per image
}

export interface ImagePricingCalculation {
  apiCostPerImage: number;
  profitMargin: number;
  totalCostPerImage: number;
}

@Injectable({
  providedIn: 'root',
})
export class ImagePricingService {
  // API Cost Configuration (based on OpenAI GPT-4o Vision pricing)
  private readonly API_COSTS = {
    // GPT-4o Vision API costs (verified 2025)
    // https://openai.com/api/pricing/
    GPT_VISION_INPUT_TOKENS_PER_1000: 0.0025, // $0.0025 per 1K input tokens
    GPT_VISION_OUTPUT_TOKENS_PER_1000: 0.01, // $0.010 per 1K output tokens
  };

  // Average tokens per image processing request (OCR/text extraction)
  private readonly TOKENS_PER_IMAGE = {
    INPUT_TOKENS: 800, // Realistic average: image (~600 tokens) + system prompt (~200 tokens)
    OUTPUT_TOKENS: 300, // Average OCR output: ~300 tokens for typical document/menu
    SYSTEM_TOKENS: 100, // System prompt and instruction overhead
  };

  // Profit margin calculation:
  // - API cost per image: ~$0.012
  // - Markup: 400% (5x multiplier)
  // - Customer pays: ~$0.06 per image
  // - Your profit: ~$0.048 per image (80% profit margin)
  // This is industry standard for OCR/vision services
  private readonly PRICE_MULTIPLIER = 5.0;

  constructor() {}

  /**
   * Calculate the cost per image based on ChatGPT Vision API usage
   */
  calculateCostPerImage(): ImagePricingCalculation {
    // Average image processing requires:
    // 1. Image input tokens (GPT-4o vision)
    // 2. System prompt tokens
    // 3. Output tokens (extracted text)

    const totalInputTokens =
      this.TOKENS_PER_IMAGE.INPUT_TOKENS + this.TOKENS_PER_IMAGE.SYSTEM_TOKENS;
    const outputTokens = this.TOKENS_PER_IMAGE.OUTPUT_TOKENS;

    // Calculate API cost per image
    const inputCostPerImage =
      (totalInputTokens / 1000) *
      this.API_COSTS.GPT_VISION_INPUT_TOKENS_PER_1000;
    const outputCostPerImage =
      (outputTokens / 1000) * this.API_COSTS.GPT_VISION_OUTPUT_TOKENS_PER_1000;

    const apiCostPerImage = inputCostPerImage + outputCostPerImage;

    // Apply price multiplier for customer pricing
    const totalCostPerImage = apiCostPerImage * this.PRICE_MULTIPLIER;

    // Calculate actual profit margin
    const profitMargin =
      (totalCostPerImage - apiCostPerImage) / totalCostPerImage;

    return {
      apiCostPerImage,
      profitMargin,
      totalCostPerImage,
    };
  }

  /**
   * Get all available image pricing tiers
   */
  getImagePricingTiers(): ImagePricingTier[] {
    const calculation = this.calculateCostPerImage();
    const basePricePerImage = calculation.totalCostPerImage;

    // Define pricing tiers with volume discounts
    const tiers: ImagePricingTier[] = [
      {
        id: 'images-20',
        name: '20 Images',
        type: 'one-time',
        images: 20,
        price: 0.5, // Fixed $0.50 minimum for Stripe
        pricePerImage: 0.025,
        description: 'Perfect for testing',
      },
      {
        id: 'images-50',
        name: '50 Images',
        type: 'one-time',
        images: 50,
        price: basePricePerImage * 50 * 0.9, // 10% discount
        originalPrice: basePricePerImage * 50,
        savings: 10,
        pricePerImage: basePricePerImage * 0.9,
        description: 'Save 10%',
      },
      {
        id: 'images-100',
        name: '100 Images',
        type: 'one-time',
        images: 100,
        price: basePricePerImage * 100 * 0.85, // 15% discount
        originalPrice: basePricePerImage * 100,
        savings: 15,
        pricePerImage: basePricePerImage * 0.85,
        description: 'Save 15%',
      },
      {
        id: 'images-200',
        name: '200 Images',
        type: 'one-time',
        images: 200,
        price: basePricePerImage * 200 * 0.8, // 20% discount
        originalPrice: basePricePerImage * 200,
        savings: 20,
        pricePerImage: basePricePerImage * 0.8,
        description: 'Save 20%',
      },
      {
        id: 'images-500',
        name: '500 Images',
        type: 'one-time',
        images: 500,
        price: basePricePerImage * 500 * 0.75, // 25% discount
        originalPrice: basePricePerImage * 500,
        savings: 25,
        pricePerImage: basePricePerImage * 0.75,
        description: 'Save 25%',
      },
      {
        id: 'images-1000',
        name: '1,000 Images',
        type: 'one-time',
        images: 1000,
        price: basePricePerImage * 1000 * 0.7, // 30% discount
        originalPrice: basePricePerImage * 1000,
        savings: 30,
        pricePerImage: basePricePerImage * 0.7,
        description: 'Save 30%',
      },
    ];

    // Round prices to nearest cent
    return tiers.map((tier) => ({
      ...tier,
      price: Math.round(tier.price * 100) / 100,
      originalPrice: tier.originalPrice
        ? Math.round(tier.originalPrice * 100) / 100
        : undefined,
      pricePerImage: tier.pricePerImage
        ? Math.round(tier.pricePerImage * 100) / 100
        : undefined,
    }));
  }

  /**
   * Get pricing tiers grouped by type
   */
  getImagePricingTiersByType(): {
    oneTime: ImagePricingTier[];
    monthly: ImagePricingTier[];
    yearly: ImagePricingTier[];
  } {
    const allTiers = this.getImagePricingTiers();
    return {
      oneTime: allTiers.filter((tier) => tier.type === 'one-time'),
      monthly: allTiers.filter((tier) => tier.type === 'monthly'),
      yearly: allTiers.filter((tier) => tier.type === 'yearly'),
    };
  }

  /**
   * Get the most popular tier for each type
   */
  getPopularImageTiers(): ImagePricingTier[] {
    const allTiers = this.getImagePricingTiers();
    return allTiers.filter((tier) => tier.popular);
  }

  /**
   * Calculate savings for a tier
   */
  calculateSavings(tier: ImagePricingTier): number {
    if (!tier.originalPrice) return 0;
    return tier.originalPrice - tier.price;
  }

  /**
   * Get pricing information for display
   */
  getPricingInfo(): {
    baseCostPerImage: number;
    profitMargin: number;
    apiCostPerImage: number;
  } {
    const calculation = this.calculateCostPerImage();
    return {
      baseCostPerImage: calculation.totalCostPerImage,
      profitMargin: calculation.profitMargin,
      apiCostPerImage: calculation.apiCostPerImage,
    };
  }
}
